import React, { useState } from 'react';
import { Send, MessageSquare, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { capRequestReview, capAddReview, capRespondReview } from '../../lib/capstoneApi';
import { inputCls, btnAmber, btnGhost, fmtDate, RAG_COLORS } from './shared';
import GuidePanel from './GuidePanel';

const REVIEW_STATUS_STYLES = {
  Pending: 'bg-white/10 text-white/60',
  Reviewed: 'bg-sky-400/15 text-sky-300',
  'Changes Requested': 'bg-amber-400/15 text-amber-300',
  Approved: 'bg-emerald-500/15 text-emerald-300',
};

export default function FeedbackTab({ project, reviews, reloadReviews, user }) {
  const isMentor = ['mentor', 'admin'].includes(project.my_role);
  const [phaseId, setPhaseId] = useState('');
  const [busy, setBusy] = useState(false);
  const phases = project.milestones || [];

  const request = async () => {
    if (!phaseId) return;
    setBusy(true);
    try { await capRequestReview(project.id, phaseId); reloadReviews(); toast.success('Review requested — your mentor has been notified'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not request review'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="cap-feedback-tab">
      {!isMentor && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-white/12 bg-slate-900/60 p-4 mb-6">
          <span className="text-[12px] text-white/60 font-semibold">Request mentor review:</span>
          <select className={`${inputCls} !w-auto flex-1 min-w-[180px]`} value={phaseId} onChange={(e) => setPhaseId(e.target.value)} data-testid="cap-review-phase-select">
            <option value="">Choose phase…</option>
            {phases.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button className={btnAmber} disabled={!phaseId || busy} onClick={request} data-testid="cap-request-review-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request review
          </button>
        </div>
      )}

      {isMentor && <MentorReviewForm project={project} reloadReviews={reloadReviews} />}

      {reviews.length === 0 && (
        <div className="text-center text-[13px] text-white/35 italic py-10">No mentor feedback yet.</div>
      )}

      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-xl border border-white/10 bg-slate-900/50 p-4" data-testid={`cap-review-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2.5 mb-2">
              <span className={`px-2.5 py-0.5 rounded-full text-[10.5px] font-bold ${REVIEW_STATUS_STYLES[r.status] || 'bg-white/10 text-white/60'}`}>{r.status}</span>
              <span className="text-[12px] text-white/70 font-semibold">{r.phase_name}</span>
              {r.rating && (
                <span className="inline-flex items-center gap-1.5 text-[10.5px] text-white/55">
                  <span className={`w-2 h-2 rounded-full ${RAG_COLORS[r.rating]}`} /> {r.rating}
                </span>
              )}
              <span className="text-[10px] text-white/30 ml-auto">{fmtDate(r.updated_at || r.created_at)}</span>
            </div>
            {r.comment ? (
              <p className="text-[13px] text-white/75 leading-relaxed">“{r.comment}” <span className="text-white/35 text-[11px]">— {r.mentor || 'Mentor'}</span></p>
            ) : (
              <p className="text-[12px] text-white/35 italic">Awaiting mentor response{r.requested_by ? ` (requested by ${r.requested_by})` : ''}.</p>
            )}
            {(r.responses || []).map((resp, i) => (
              <div key={i} className="mt-2.5 ml-4 pl-3 border-l-2 border-white/10 text-[12.5px] text-white/60">
                {resp.text} <span className="text-white/30 text-[10.5px]">— {resp.by}, {fmtDate(resp.at)}</span>
              </div>
            ))}
            {r.comment && !isMentor && <ReplyBox reviewId={r.id} reloadReviews={reloadReviews} />}
            {r.status === 'Changes Requested' && !isMentor && (
              <GuidePanel
                topic="mentor_reply"
                projectId={project.id}
                label="Ask Guide how to approach this revision"
                onUse={(text) => { navigator.clipboard?.writeText(text); toast.success('Approach copied — use it to plan your revision'); }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ReplyBox = ({ reviewId, reloadReviews }) => {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  if (!open) return <button className="mt-2 text-[11px] text-amber-300/80 hover:text-amber-200 inline-flex items-center gap-1" onClick={() => setOpen(true)}><MessageSquare className="w-3 h-3" /> Respond</button>;
  const submit = async () => {
    if (!text.trim()) return;
    await capRespondReview(reviewId, text.trim());
    setText(''); setOpen(false); reloadReviews();
  };
  return (
    <div className="flex gap-2 mt-3">
      <input className={`${inputCls} flex-1`} placeholder="Your response…" value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} data-testid="cap-review-reply-input" />
      <button className={btnGhost} disabled={!text.trim()} onClick={submit}>
        <Send className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const MentorReviewForm = ({ project, reloadReviews }) => {
  const [form, setForm] = useState({ phase_id: '', comment: '', rating: '', status: 'Reviewed' });
  const [busy, setBusy] = useState(false);
  const phases = project.milestones || [];
  const submit = async () => {
    setBusy(true);
    try {
      await capAddReview(project.id, { ...form, rating: form.rating || null });
      reloadReviews();
      setForm({ phase_id: '', comment: '', rating: '', status: 'Reviewed' });
      toast.success('Feedback recorded — the team has been notified');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save review'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-sky-400/25 bg-sky-400/[0.04] p-4 mb-6" data-testid="cap-mentor-review-form">
      <div className="text-[10px] tracking-[0.22em] font-bold uppercase text-sky-300 mb-3">Leave mentor feedback</div>
      <div className="grid sm:grid-cols-3 gap-2.5 mb-2.5">
        <select className={inputCls} value={form.phase_id} onChange={(e) => setForm({ ...form, phase_id: e.target.value })} data-testid="cap-mentor-phase-select">
          <option value="">Phase…</option>
          {phases.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="cap-mentor-rag-select">
          <option value="">RAG rating…</option>
          <option>Green</option><option>Amber</option><option>Red</option>
        </select>
        <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="cap-mentor-status-select">
          <option>Reviewed</option><option>Approved</option><option>Changes Requested</option>
        </select>
      </div>
      <textarea rows={2} className={`${inputCls} resize-y mb-2.5`} placeholder="Your comment for the team…" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="cap-mentor-comment-input" />
      <div className="flex gap-2">
        <button className={btnAmber} disabled={!form.phase_id || busy} onClick={submit} data-testid="cap-mentor-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : form.status === 'Approved' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} Submit feedback
        </button>
      </div>
    </div>
  );
};
